import React, { useState, useRef, useEffect } from 'react';
import '../App.css';

interface EmailVerificationScreenProps {
  email: string;
  onVerified: (token: string) => void;
  onResend: () => void;
  onBack: () => void;
}

const EmailVerificationScreen: React.FC<EmailVerificationScreenProps> = ({
  email,
  onVerified,
  onResend,
  onBack,
}) => {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(15 * 60); // 15 minutes in seconds

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for code expiration
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Format time remaining as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle input change
  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every((digit) => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only accept 6-digit codes
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      setError('');

      // Focus last input
      inputRefs.current[5]?.focus();

      // Auto-submit
      handleSubmit(pastedData);
    } else {
      setError('Please paste a valid 6-digit code');
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Submit verification code
  const handleSubmit = async (codeString?: string) => {
    const verificationCode = codeString || code.join('');

    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBaseURL}/api/v1/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          code: verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Verification successful
        if (data.token) {
          onVerified(data.token);
        } else {
          setError('Verification successful! Please login.');
          setTimeout(() => onBack(), 2000);
        }
      } else {
        setError(data.error || 'Invalid verification code');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
      console.error('Verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBaseURL}/api/v1/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendCooldown(60); // 60 second cooldown
        setTimeRemaining(15 * 60); // Reset timer
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        onResend();
      } else {
        setError(data.error || 'Failed to resend code');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
      console.error('Resend error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-card">
        <div className="verification-header">
          <h2>Check Your Email!</h2>
          <p className="verification-subtitle">
            We've sent a 6-digit code to <strong>{email}</strong>. Enter it below to continue.
          </p>
        </div>

        <div className="code-input-container">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`code-input ${error ? 'code-input-error' : ''}`}
              disabled={loading}
              autoComplete="off"
            />
          ))}
        </div>

        {error && <div className="verification-error">{error}</div>}

        <div className="verification-timer">
          {timeRemaining > 0 ? (
            <p>Your code expires in {formatTime(timeRemaining)}</p>
          ) : (
            <p className="timer-expired">Oops! Your code expired. No worries, just request a new one below.</p>
          )}
        </div>

        <div className="verification-actions">
          <button
            onClick={() => handleSubmit()}
            disabled={loading || code.join('').length !== 6}
            className="btn-verify"
          >
            {loading ? 'Verifying...' : 'Verify My Email'}
          </button>

          <button
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            className="btn-resend"
          >
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend Code'}
          </button>

          <button onClick={onBack} className="btn-back" disabled={loading}>
            Go Back
          </button>
        </div>

        <div className="verification-help">
          <p>Didn't receive the code?</p>
          <ul>
            <li>Check your spam folder</li>
            <li>Make sure {email} is correct</li>
            <li>Wait a few moments and try resending</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;
